<template>
  <v-container class="d-grid-center-align">
    <v-card width="500px" class="rounded-lg">
      <v-card-title> Add a new user </v-card-title>
      <v-card-text>
        <v-btn icon exact :to="{ name: 'users' }" class="d-flex flex-column justify-self-end user-creation__close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
        <v-form v-model="valid" ref="user_form" class="user-creation__form">
          <v-text-field
            :rules="requiredRule"
            v-model="userForm.company_name"
            outlined
            label="Company Name"
            dense
            class="rounded-lg"
            placeholder="Company"
          />
          <v-text-field
            v-model="userForm.contact_person"
            :rules="requiredRule"
            outlined
            label="Contact Person"
            dense
            class="rounded-lg"
            placeholder="Contact Person"
          />
          <v-text-field
            v-model="userForm.address"
            :rules="requiredRule"
            outlined
            dense
            label="Address"
            class="rounded-lg"
            placeholder="Address"
          />
          <v-text-field
            :rules="cityValidationRule"
            v-model="userForm.city"
            outlined
            dense
            label="City"
            class="rounded-lg"
            placeholder="City"
          />
          <v-select
            :rules="requiredRule"
            :items="provinces"
            clearable
            v-model="userForm.province"
            outlined
            dense
            label="Province"
            class="rounded-lg"
            placeholder="Province"
          />
          <v-text-field
            :rules="emailValidationRule"
            v-model="userForm.email"
            outlined
            dense
            label="Email Address"
            class="rounded-lg"
            placeholder="Email Address"
          />
          <v-text-field
            v-model="userForm.phone_number"
            :rules="requiredRule"
            outlined
            dense
            label="Phone Number"
            class="rounded-lg"
            placeholder="Phone Number"
          />
          <v-text-field
            :rules="numberRule"
            v-model="userForm.margin_rate"
            type="number"
            outlined
            :min="0"
            :step="1"
            dense
            label="Margin Rate"
            class="rounded-lg"
            placeholder="Margin Rate"
          />
          <v-select
            :rules="requiredRule"
            clearable
            v-model="userForm.role"
            :items="roles"
            outlined
            dense
            label="Customer Type"
            class="rounded-lg"
            placeholder="Customer Type"
          />
          <v-text-field
            v-if="!isEdit"
            :rules="passwordValidation"
            v-model="userForm.password"
            type="password"
            outlined
            dense
            label="Password"
            class="rounded-lg"
            placeholder="Password"
          />
          <v-text-field
            v-if="!isEdit"
            :rules="[passwordsMatch]"
            v-model="userForm.password_confirmation"
            type="password"
            outlined
            dense
            label="Confirm Password"
            class="rounded-lg"
            placeholder="Confirm Password"
          />
          <v-row>
            <v-col>
              <v-btn block @click="resetFormOrGoBack" class="error">
                Cancel
              </v-btn>
            </v-col>
            <v-col>
              <v-btn block @click="validateAddOrEditUser" class="success ml-2">
                {{ isEdit ? "update" : "Create User" }}
              </v-btn>
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>
    </v-card>
    <error-alert
      class="mt-5"
      dismissible
      :message.sync="validationErrorMessage"
      :validation-messages="validationErrorArray"
    ></error-alert>
    <v-alert type="success" dismissible v-model="showAlert">{{ alertMessage }}</v-alert>
  </v-container>
</template>

<script>
import { roles, provinces } from "@/constants";
import { extractDataAxios, extractErrorMessageAxios, extractValidationErrorsAxios } from "@/httpClient";
import ErrorAlert from "@/components/ErrorAlert";
const requiredValidation = (v) => !!v || "required";
const regexValidation = (regex, v, error) => regex.test(v) || error;
const lengthValidation = (v) =>
  regexValidation(/^(?=.*[0-9])(?=.*[a-z])(?=.*[A-Z]).{10,32}$/, v, "Password must be atleast 10 characters and at most 32 charactes, with one smallcase letter, one UPPERCASE letter, and one digit.");
export default {
  name: "AddEditUser",
  props: {
    isEdit: Boolean,
  },
  components: {
    ErrorAlert,
  },
  async created() {
    if (this.isEdit) {
      await this.fetchuser();
    }
  },
  data() {
    return {
      valid: false,
      userForm: {
        company_name: null,
        contact_person: null,
        address: null,
        city: null,
        province: null,
        email: null,
        phone_number: null,
        margin_rate: null,
        role: null,
        password: "",
        password_confirmation: "",
      },
      initialData: null,
      roles,
      provinces,
      requiredRule: [requiredValidation],
      emailValidationRule: [requiredValidation, (v) => regexValidation(/^.+@.+$/, v, "Email is not valid!")],
      cityValidationRule: [
        requiredValidation,
        (v) => regexValidation(/\w+/, v, "City cannot contain numbers or symbols!"),
      ],
      marginValidation: [requiredValidation, (v) => regexValidation(/[[::digit::]]/, v, "margin must be number!")],
      passwordValidation: [requiredValidation, lengthValidation],
      numberRule: [
        (v) => (v !== null && v !== undefined && v !== "") || "required",
        (v) => (/^-?[0-9]+$/.test(v) && v >= 0) || "Must be a positive whole number",
      ],
      passwordsMatch: (v) => v === this.userForm.password || "Passwords must match!",
      validationErrorArray: [],
      validationErrorMessage: "",
      showAlert: false,
      alertMessage: "",
    };
  },
  computed: {
    isClean() {
      return Object.values(this.userForm).every((userFormField) => userFormField === null);
    },
  },
  methods: {
    resetFormOrGoBack() {
      this.isEdit || this.isClean ? this.$router.back() : this.$refs.user_form.reset();
    },
    async validateAddOrEditUser() {
      if (this.$refs.user_form.validate()) {
        this.isEdit ? this.updateUser() : this.registerUser();
      }
    },
    async registerUser() {
      try {
        await window.axios.get("/sanctum/csrf-cookie");
        await window.axios.post("/api/register", this.userForm);
        this.resetFormOrGoBack();
        this.$router.push({ name: "users" });
      } catch (e) {
        this.validationErrorArray = extractValidationErrorsAxios(e);
        this.validationErrorMessage = extractErrorMessageAxios(e);
      }
    },
    async updateUser() {
      try {
        const user = await window.axios.put("/api/users/" + this.$route.params.id, this.userForm);
        this.type = "success";
        this.showAlert = true;
        this.alertMessage = "Updated User!";
        // this.$router.push({ name: "users" });
      } catch (e) {
        this.validationErrorArray = extractValidationErrorsAxios(e);
        this.validationErrorMessage = extractErrorMessageAxios(e);
      }
    },
    async fetchuser() {
      const userData = extractDataAxios(await window.axios.get("/api/users/" + this.$route.params.id));
      for (let key in this.userForm) {
        this.userForm[key] = userData[key];
      }
      this.initialData = {
        ...userData,
        password: "",
        password_confirmation: "",
      };
    },
  },
};
</script>

<style scoped>
.user-creation__close {
  right: 0;
  top: 0;
  margin: 15px;
  position: absolute;
}
.user-creation__form {
  padding: 15px;
}
</style>
